import { useAppContext } from '../context/AppContext';

/**
 * Custom hook to check user permissions
 * 
 * @returns {Object} Permission checking functions and loading state
 */
export function usePermissions() {
  const { state } = useAppContext();
  
  /**
   * Check if user has permission for a specific resource and action
   * Handles wildcards (* resource or * action)
   * 
   * @param {string} resource - Resource name (e.g., 'memories', 'supervisor')
   * @param {string} action - Action name (e.g., 'read', 'write', 'query')
   * @returns {boolean} True if user has permission
   */
  const hasPermission = (resource, action) => {
    // If permissions haven't loaded yet, deny access (safe default)
    if (!state.permissionsLoaded) {
      return false;
    }
    
    // If no permissions loaded, deny access
    if (!state.userPermissions || state.userPermissions.length === 0) {
      return false;
    }
    
    // Check if user has the required permission
    // Permission matches if:
    // 1. Resource is wildcard (*) OR matches exactly
    // 2. Action is wildcard (*) OR is in the actions array
    return state.userPermissions.some(permission => {
      const resourceMatch = permission.resource === '*' || permission.resource === resource;
      const actionMatch = permission.actions.includes('*') || permission.actions.includes(action);
      
      return resourceMatch && actionMatch;
    });
  };
  
  /**
   * Check if user has at least one permission from a list
   * 
   * @param {Array} permissionsList - Array of {resource, action} objects
   * @returns {boolean} True if user has at least one permission
   */
  const hasAnyPermission = (permissionsList) => {
    if (!state.permissionsLoaded) {
      return false;
    }
    
    return permissionsList.some(({ resource, action }) => 
      hasPermission(resource, action)
    );
  };
  
  /**
   * Check if user has all permissions from a list
   * 
   * @param {Array} permissionsList - Array of {resource, action} objects
   * @returns {boolean} True if user has all permissions
   */
  const hasAllPermissions = (permissionsList) => {
    if (!state.permissionsLoaded) {
      return false;
    }
    
    return permissionsList.every(({ resource, action }) => 
      hasPermission(resource, action)
    );
  };
  
  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    permissionsLoaded: state.permissionsLoaded,
    permissions: state.userPermissions
  };
}

